// @flow

import React, { Component } from 'react';
import Range from 'rc-slider/lib/Range';
import _ from 'lodash';
import mapSeries from 'async/mapSeries';

import App from './App.jsx';

import {takeDir, takeFilename, mode} from './utils.js';
import {fetch_png, get_tif_friend_of, get_tif_rect, get_pixel_size, copy_culti} from './fetch_things.js';

import './css/Diff.css';

import type {T_ErrNum, T_Friends, T_Fun, T_TrimResult, T_Next, T_Extent, T_ErrOk} from './types.js';
import type {T_ImgFile} from './Img.js';

const moment = require('moment');


type T_DiffProps = {
  paths : Array<string>,      // paths de los pngs
  cultis: Array<string>,      // paths de los cultis. cultis[i] es el cultivable del mapa paths[i]
  app   : App
};



type T_Day = {
  sdate: string,
  path : string,
  at   : number
};



type T_DiffState = {
  days  : Array<T_Day>,
  max   : number,
  values: Array<number>,
  marks : {[number]: string},
  gmax  : number,
  png1  : string,
  png2  : string
};



type T_Group = {
  culti: string,        // todos estos son full paths
  png  : string,
  tif  : string,
  psize: number,        // tamaño del pixel del tif, el metros.
  rect : T_Extent,      // extent del raster, o sea, las coordenadas {upperLeft, lowerRight}, ambos [num, num] MÁS size: [num, num]
  umap : string         // URL del mapa png
};



export default class Diff extends Component<T_DiffProps, T_DiffState> {
  last_diff     : string = '';
  clipped       : boolean = false;
  zero_fixed    : boolean = false;
  pixels_resized: boolean = false;
  culti_copied  : boolean = false;
  groups        : Array<T_Group> = [];
  
  
  constructor(props: T_DiffProps) {
    super(props);
    
    const sdates  = this.props.paths.map(path => takeFilename(takeDir(path))),
          moments = sdates.map(sdate => moment(sdate, 'YYYY-MM-DD')),
          days    = _.sortBy(moments.map((date, index) => {
                      return {
                        sdate: sdates[index],
                        path: this.props.paths[index],
                        at: Math.abs(date.diff(moments[0], 'days'))
                      };
                    }), 'at'),
          max     = _.maxBy(days, 'at').at,
          marks   = {};
    
    days.forEach((day) => {
      marks[`${day.at}`] = day.sdate;
    });
    
    this.state = {
      days,
      max,
      values: [0, days[1].at],
      marks,
      gmax: 666,
      png1: '',
      png2: ''
    };
    
    this.props.app.diff = this;
  }
  
  
  
  abort = (msg: string): void => {
    console.log(msg);                         // FIXME: muestra un error como la gente.
    
    this.props.app.setState({bios: []});
  }
  
  
  
  componentWillUnmount() {
    this.props.app.diff = null;
  }
  
  
  
  componentDidMount() {
    this.change_range(this.state.values);
  }
  
  
  
  set_groups = (next: T_Next): void => {
    if (this.groups.length > 0) {
      next(true);
    }
    else {
      const {app} = this.props;
      
      let index = -1;
      
      mapSeries(this.props.cultis, (culti, callback) => {
        index++;
        
        fetch_png(app, this.props.paths[index], (result: T_ImgFile | string) => {
          if (typeof result === 'string') {
            callback(null, false);
          }
          else {
            const umap = result.nodeUrl;
            
            get_tif_friend_of(app, this.props.paths[index], (trio: ?T_Friends) => {
              if (trio) {
                const tif = `${takeDir(culti)}/${trio.tif}`;
                
                get_pixel_size(app, tif, (mb_num: T_ErrNum) => {
                  if (typeof mb_num === 'number') {
                    const psize: number = mb_num;
                    
                    get_tif_rect(app, tif, (mb_ext: ?T_Extent) => {
                      if (mb_ext != null) {
                        this.groups.push({
                          culti,
                          png: this.props.paths[index],
                          tif,
                          psize,
                          rect: mb_ext,     // NOTE: rect incluye ahora un size de tipo [num, num]
                          umap
                        });
                      }
                      
                      callback(null, false);
                    });
                  }
                  else {
                    callback(null, false);
                  }
                });
              }
              else {
                callback(null, false);
              }
            });
          }
        });
      }, (err, rs) => {
        if (this.groups.length !== this.props.cultis.length || this.props.cultis.length !== this.props.paths.length) {
          this.abort('No pude obtener el raster asociado a alguno de los cultivables.');
        }
        else {
          next(true);
        }
      });
    }
  };
  
  
  
  clip_if = (next: T_Next): void => {
    if (this.clipped) {
      next(true);
    }
    else {
      const {app} = this.props,
            url_clip = (path: string): string => `${app.remote}/recortar-mapas/?culti=${path}&session_id=${app.session()}`;
      
      mapSeries(this.props.cultis, (culti, callback) => {
        fetch(url_clip(culti)).then(resp => resp.json()).then((response: T_TrimResult) => {
          callback(null, true);
        });
      }, (err, rs) => {
        this.clipped = true;
        
        next(true);
      });
    }
  };
  
  
  
  fix_zero_if = (next: T_Next): void => {
    if (this.zero_fixed) {
      next(true);
    }
    else {
      const {app} = this.props,
            url_fix = (path: string): string => `${app.remote}/fix-zeros/?culti=${path}&session_id=${app.session()}`;
      
      mapSeries(this.props.cultis, (culti, callback) => {
        fetch(url_fix(culti)).then(resp => resp.json()).then((response: T_TrimResult) => {
          callback(null, true);
        });
      }, (err, rs) => {
        this.zero_fixed = true;
        
        next(true);
      });
    }
  };
  
  
  
  resize_pixel_sizes_if = (next: T_Next): void => {
    if (this.pixels_resized) {
      next(true);
    }
    else {
      const psizes = _.map(this.groups, 'psize').sort();    // en orden ascendente.
      
      if (_.uniq(psizes).length === 1) {
        this.pixels_resized = true;
        
        next(true);
      }
      else {
        const {app}      = this.props,
              gpsize     = mode(psizes),              // el mayor de los pixel sizes más repetido (por si hay empate).
              wrong_grps = this.groups.filter(group => group.psize !== gpsize);
        
        mapSeries(wrong_grps, (group: T_Group, callback) => {
          const [w,h] = group.rect.size,
                url = app.url('resize-tif', `tif=${group.tif}&psize_from=${group.psize}&psize_to=${gpsize}&w=${w}&h=${h}`);
          
          fetch(url).then(resp => resp.json()).then((response: T_ErrOk) => {
            callback(null, true);
          });
        }, (err, rs) => {
          this.pixels_resized = true;
          
          next(true);
        });
      }
    }
  };
  
  
  
  copy_culti_if = (dir: string, next: T_Next): void => {
    if (this.culti_copied) {
      next(true);
    }
    else {
      copy_culti(this.props.app, this.props.cultis[0], dir, (result_cp) => {
        next(true);
      });
    }
  };
  
  
  
  get_raster_of = (png_path: string): string => {
    const group = this.groups.find(group => group.png === png_path);
    
    return group ? group.tif : '';
  };
  
  
  
  get_url_of = (png_path: string): string => {
    const group = this.groups.find(group => group.png === png_path);
    
    return group ? group.umap : '';
  };
  
  
  
  change_range = (values: Array<number>): void => {
    this.setState({values, png1: '', png2: ''});
    
    const day1 = this.state.days.find(day => day.at === _.min(values)),
          day2 = this.state.days.find(day => day.at === _.max(values));
    
    const close_png = (png_path: string, next: T_Fun) => {
      if (png_path) {
        const it = this.props.app.get_item_png(png_path);
        
        if (it)
          it.close(next);
        else
          next();
      }
      else
        next();
    };
    
    if (day1 != null && day2 != null && day1.path !== day2.path) {
      const path1 = day1.path,
            path2 = day2.path,
            dir1  = takeDir(path1),
            dir2  = takeDir(path2),
            {app} = this.props,
            pdir = takeDir(dir1),
            odir = `${pdir}/Mapas_Diferenciales`,
            out  = `${odir}/diff_${takeFilename(dir1)}_${takeFilename(dir2)}.png`;
      
      const open_dir = (dir: string, next: T_Fun) => {
        const it = app.get_item_dir(dir1);
        
        if (it) {
          if (it.state.Open)
            next();
          else
            it.open(() => next());
        }
        else
          next();
      };
      
      const show_odir = (next: T_Fun) => {
        const it = app.get_item_dir(pdir);
        
        if (it && ! it.dirs.find(dir => dir.get_path() === odir))
          app.reload_dir(pdir, next);
        else
          next();
      };
      
      const open_refresh_odir = (target: string, next: T_Fun) => {
        const it = app.get_item_dir(odir);
        
        if (it && it.state.Open && ! it.pngs.find(png => png.get_path() === target))
          app.reload_dir(odir, next);
        else if (it && ! it.state.Open)
          it.open(next);
        else
          next();
      };
      
      this.last_diff = out;
      
      open_dir(dir1, () => {
        open_dir(dir2, () => {
          close_png(this.last_diff, () => {
            close_png(path1, () => {
              close_png(path2, () => {
                this.copy_culti_if(odir, () => {
                  this.clip_if(() => {
                    this.fix_zero_if(() => {
                      this.set_groups(() => {
                        const tif1 = this.get_raster_of(path1),
                              tif2 = this.get_raster_of(path2),
                              url  = `${app.remote}/raster-diff/?session_id=${app.session()}&tif1=${tif1}&tif2=${tif2}&out=${out}`;
                        
                        this.resize_pixel_sizes_if(() => {
                          fetch(url).then(resp => resp.json()).then((response: T_ErrNum) => {
                            if (response.error) {
                              console.log(response.error);
                              
                              const it = app.get_item_dir(odir);
                              
                              if (it) {
                                it.pngs.forEach((png) => {
                                  if (png.state.Loaded) {
                                    close_png(png.get_path(), _.noop);
                                  }
                                });
                              }
                            }
                            else if (typeof response === 'number') {
                              const gmax: number = response;
                              
                              this.setState({gmax, png1: this.get_url_of(path1), png2: this.get_url_of(path2)}, () => {
                                app.kill_these(app.map_ui.map.imgs_loaded(), () => {
                                  show_odir(() => {
                                    open_refresh_odir(out, () => {
                                      const it = app.get_item_png(out);
                                      
                                      if (it)
                                        it.open();
                                    });
                                  });
                                });
                              });
                            }
                          });
                        });
                      });
                    });
                  });
                });
              });
            });
          });
        });
      });
    }
    else {
      close_png(this.last_diff, _.noop);
    }
  }
  
  
  
  mini_png_left = (which: 0 | 1): number => {
    const {max} = this.state,
          val   = this.state.values[which],
          w0    = (this.props.app.props.width - 5) * 0.7;
    
    return ((w0 - 80) * (val / max)) + 28;
  };
  
  
  
  render() {
    if (this.props.paths.length < 2)
      return null;
    
    return (
      <div className='divDiff'>
        {this.state.png1 !== '' &&
          <div className='diff_png1' style={{left: this.mini_png_left(0)}}>
            <img src={this.state.png1} alt='png1' />
          </div>
        }
        
        <Range
           min={0}
           max={this.state.max}
           step={null}
           marks={this.state.marks}
           allowCross={false}
           pushable={true}
           value={this.state.values}
           onChange={this.change_range}
        />
        
        {this.state.png2 !== '' &&
          <div className='diff_png2' style={{left: this.mini_png_left(1)}}>
            <img src={this.state.png2} alt='png2' />
          </div>
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/Diff.jsx